import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  BOTTOM_SHEET_TOP_HANDLER_HEIGHT,
  BOTTOM_SHEET_TOP_HANDLER_PLACEHOLDER_HEIGHT,
} from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns `Map` component `offset` if the following requirements are met:
 *
 * - The current breakpoint is `XS` (aka Mobile) (we need to use offset only when the bottom sheet used).
 * - The location search type if not "Delivery", since the bottom sheet in not displayed in that case.
 * - At least one location has been resolved.
 * - The bottom sheet has been expanded.
 * - The height of the first location card is known (we use the first location card as a main offset source).
 */
export const useLocationsMapOffset = (props: UseMapOffsetProps) => {
  const {
    firstLocationCardHeight = 0,
    isDeliverySearchType,
    isLocationsBottomSheetCollapsed,
    locations,
  } = props;

  const isMapsOffsetEnabled = useFeatureFlag(
    'XP-3179-short-maps-offset-enabled',
  );
  const { currentBreakpoint } = useResponsive();

  if (!isMapsOffsetEnabled) return;

  const hasLocations = locations.length > 0;

  const shouldUseVerticalOffset =
    currentBreakpoint.isMobile &&
    hasLocations &&
    !isLocationsBottomSheetCollapsed &&
    !isDeliverySearchType;

  const bottomSheetHeaderOffset =
    locations.length > 1
      ? BOTTOM_SHEET_TOP_HANDLER_HEIGHT
      : BOTTOM_SHEET_TOP_HANDLER_PLACEHOLDER_HEIGHT;

  const offsetY =
    firstLocationCardHeight > 0
      ? firstLocationCardHeight + bottomSheetHeaderOffset
      : 0;

  return shouldUseVerticalOffset ? offsetY : undefined;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMapOffsetProps = {
  readonly firstLocationCardHeight: number;
  readonly locations: ReadonlyArray<Record<string, unknown>>;
  readonly isDeliverySearchType: boolean;
  readonly isLocationsBottomSheetCollapsed: boolean;
};
